import { AnyWindowEntity,WindowEntity } from 'types/windows';
import { WindowWithApplicationState } from '../../../types/appState';

import { FileSkeleton, type Note } from '../../../types';
import type { BaseEntityType } from '../../../types/entities';

import type { AppIcon as AppIconType } from '../../../types/apps';
import { DocsAppDefinition } from '../../../apps/DocsApp/definition';
import { EntityBrowserApp2Definition } from '../../../apps/EntityBrowserApp2/definition';
import { SettingsAppDefinition } from '../../../apps/SettingsApp/definition';
import { iconService } from '../../../services/iconService';

/**
 * Centralized helper to extract entityId from a window
 * Priority 1: Root level entityId (new standard)
 * Priority 2: ApplicationState (legacy fallback for backward compatibility)
 */
export function getWindowEntityId(window: WindowEntity): string | undefined {
  if (window.entityId) return window.entityId;
  
  return window.applicationState?.docs?.activeDocId ||
         window.applicationState?.pdfium?.entityId ||
         window.applicationState?.notes?.activeNoteId ||
         window.applicationState?.notes?.selectedNoteId ||
         window.applicationState?.entityView?.entityId;
}

/**
 * Centralized helper to extract entityLlmId from a window
 */
export function getWindowEntityLlmId(window: WindowEntity): string | undefined {
  return window.entityLlmId;
}

// Helper function to get app icon based on app type
export const getAppIcon = (appType?: string): string | AppIconType => {
  if (!appType) return '⬜️';
  
  // Direct mapping to app definitions for known apps
  const appDefinitions: Record<string, AppIconType | string | undefined> = {
    'docs': DocsAppDefinition.icon,
    'entityBrowser2': EntityBrowserApp2Definition?.icon,
    'settings': SettingsAppDefinition?.icon,
  };
  
  if (appDefinitions[appType]) {
    return appDefinitions[appType] as string | AppIconType;
  }
  
  // Fallback to hardcoded icons if app not found
  const appIconMap: Record<string, string | AppIconType> = {
    'docs': '📄',
    'chat': '💬', 
    'browser': '🌐',
    'console': '⚙️',
    'entityBrowser': '📁',
    'entityBrowser2': '📁',
    'settings': '⚙️',
    'notes': '📝',
    'pdfium': '📕',
    'pdf': '📕',
    'mediaviewer': '🖼️',
    'bank': '🏦',
    'webdoc': '🌐',
    'disco': '📊',
    'google-drive-file': { type: 'iconify', iconifyIcon: 'simple-icons:googledrive' } as AppIconType,
  };
  return appIconMap[appType] || '⬜️';
};

export const generateWindowId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  return `window-${timestamp}-${random}`;
};

export const getAppTitle = (appTypeOrWindow: string | WindowWithApplicationState): string => {
  // If an object is passed, it might be a window with application state
  if (typeof appTypeOrWindow === 'object') {
    const window = appTypeOrWindow;
    
    // Check for docs application state
    if (window.applicationState?.docs) {
      const docsState = window.applicationState.docs;
      
      // If we're editing a document, prefer entity/recentDocs name over transient CRDT name to avoid duplication
      if (docsState.currentState === 'editing') {
        if (docsState.recentDocs && docsState.activeDocId) {
          const activeDoc = docsState.recentDocs.find((doc) => doc.docId === docsState.activeDocId);
          if (activeDoc?.name) return activeDoc.name;
        }
        if (docsState.currentDocName) return docsState.currentDocName;
      }
      
      // Default docs title
      return 'Documents';
    }
    
    // For other app types, get the app type from the window
    const appType = window.appType || '';
    // Use the appType string to lookup a title (avoid recursion)
    return getAppTitleByType(appType);
  }
  
  // String-based app type lookup
  return getAppTitleByType(appTypeOrWindow);
};

// Helper function to get app title by type string
export const getAppTitleByType = (appType: string): string => {
  switch (appType) {
    case 'chat':
      return 'Chat';
    case 'system':
    case 'entityBrowser':
      return 'Entity Browser';
    case 'entityBrowser2':
      return 'Drive';
    case 'console':
      return 'Console';
    case 'pdf':
      return 'PDF Viewer';
    case 'pdfium':
      return 'PDFium Viewer';
    case 'notes':
      return 'Notes';
    case 'docs':
      return 'Documents';
    case 'browser':
      return 'Browser';
    case 'webdoc':
      return 'Web Document';
    case 'settings':
      return 'Settings';
    case 'mediaviewer':
      return 'Media Viewer';
    case 'bank':
      return 'Bank';
    case 'disco':
      return 'Disco - Data Analysis';
    case 'prompt':
      return 'Prompt';
    case 'google-drive-file':
      return 'Google Drive File';
    case 'mail':
      return 'Mail';
    case 'contacts':
      return 'Contacts';
    case 'automations':
      return 'Automations';
    case 'app-builder':
      return 'App Builder';
    default:
      return 'Window';
  }
};

export const getAppTypeByExt = (ext: string): string | null => {
  switch (ext.toLowerCase()) {
    case '.pdf':
      return 'pdfium';
    case '.txt':
    case '.md':
    case '.log': // Common text file
      return 'file-viewer'; // ✅ Text files in readonly viewer
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.gif':
    case '.webp':
    case '.bmp':
      return 'mediaviewer';
    case '.mp4':
    case '.webm':
    case '.mov':
    case '.avi':
    case '.mkv':
      return 'mediaviewer'; // Core media app for video
    // ✅ Code files in readonly viewer
    case '.py':
    case '.js':
    case '.ts':
    case '.tsx':
    case '.jsx':
    case '.java':
    case '.cpp':
    case '.c':
    case '.h':
    case '.cs':
    case '.go':
    case '.rs':
    case '.php':
    case '.rb':
    case '.swift':
    case '.html':
    case '.css':
    case '.scss':
    case '.sass':
    case '.less':
    case '.xml':
    case '.yaml':
    case '.yml':
    case '.toml':
    case '.ini':
    case '.cfg':
    case '.conf':
    case '.sh':
    case '.bash':
    case '.zsh':
    case '.fish':
    case '.sql':
    case '.r':
    case '.lua':
    case '.perl':
    case '.dockerfile':
    case '.makefile':
    case '.cmake':
    case '.vim':
      return 'file-viewer';
    // ✅ Data files in readonly viewer
    case '.json':
    case '.csv':
      return 'file-viewer';
    // ✅ Audio files in readonly viewer
    case '.mp3':
    case '.wav':
    case '.ogg':
    case '.flac':
    case '.aac':
    case '.m4a':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Archive files in readonly viewer
    case '.zip':
    case '.tar':
    case '.gz':
    case '.rar':
    case '.7z':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Spreadsheet files to core app
    case '.xlsx':
    case '.xls':
    case '.ods':
      return 'sheets-v2';
    // ✅ Presentation files to core app
    case '.pptx':
    case '.ppt':
    case '.odp':
      return 'presentations-v2';
    default:
      return null;
  }
};

export const getAppTypeByEntity = (entity: BaseEntityType): string | null => {
  console.log('[WINDOW_UTILS] getAppTypeByEntity called:', { entityType: entity.entityType, name: entity.name });
  
  // Rule entities open in RulesApp
  if (entity.entityType === 'Rule') {
    console.log('[WINDOW_UTILS] Matched Rule entity type, returning rules');
    return 'rules';
  }

  // Handle Mail entities - these should open in EntityViewApp for entity-specific views
  if (entity.entityType === 'Mail.Draft' || 
      entity.entityType === 'Mail.Thread' || 
      entity.entityType === 'Mail.Message' ||
      entity.entityType === 'Mail.Contact' ||
      entity.entityType === 'Mail.Audience' ||
      entity.entityType === 'Mail.Broadcast') {
    console.log('[WINDOW_UTILS] Matched Mail entity type, returning entity-view', { entityType: entity.entityType });
    return 'entity-view';
  }
  
  // Handle hierarchical PDF types directly
  if (entity.entityType === 'PDF' || 
      entity.entityType === 'File.Document.PDF' || 
      entity.entityType?.startsWith('File.Document.PDF')) {
    console.log('[WINDOW_UTILS] Matched PDF type, returning pdfium');
    return 'pdfium';
  }

  // Handle hierarchical image types directly  
  if (entity.entityType?.startsWith('File.Image')) {
    console.log('[WINDOW_UTILS] Matched image type, returning mediaviewer');
    return 'mediaviewer';
  }

  // Handle hierarchical video types directly  
  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning mediaviewer');
    return 'mediaviewer';
  }

  // Handle data files (CSV, JSON, XML, etc.)
  if (entity.entityType?.startsWith('File.Data')) {
    console.log('[WINDOW_UTILS] Matched data type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with table/JSON display
  }

  // Handle code files
  if (entity.entityType?.startsWith('File.Code')) {
    console.log('[WINDOW_UTILS] Matched code type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with syntax highlighting
  }

  // Handle text/markdown documents
  if (entity.entityType?.startsWith('File.Document.Text') || 
      entity.entityType?.startsWith('File.Document.Markdown')) {
    console.log('[WINDOW_UTILS] Matched text document type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer
  }

  // Handle spreadsheet files
  if (entity.entityType?.startsWith('File.Spreadsheet')) {
    console.log('[WINDOW_UTILS] Matched spreadsheet type, returning sheets-v2');
    return 'sheets-v2'; // Core spreadsheet app
  }

  // Handle audio/video files
  if (entity.entityType?.startsWith('File.Audio')) {
    console.log('[WINDOW_UTILS] Matched audio type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning mediaviewer');
    return 'mediaviewer'; // Core media app
  }

  // Handle archive files
  if (entity.entityType?.startsWith('File.Archive')) {
    console.log('[WINDOW_UTILS] Matched archive type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  // Legacy PDF detection by MIME type
  if ((entity?.skeleton as FileSkeleton)?.['@type'] === 'File' && 
      (entity?.skeleton as FileSkeleton)?.mimeType === 'application/pdf') {
    console.log('[WINDOW_UTILS] Matched legacy PDF by MIME type, returning pdfium');
    return 'pdfium';
  }

  // Handle any file type (legacy "File" or hierarchical "File.*")
  if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // Use skeleton.fileName first, fallback to entity.name
    const fileName = ((entity.skeleton as any)?.fileName || entity.name || '').toLowerCase();
    const ext = fileName.substring(fileName.lastIndexOf('.'));
    const appType = getAppTypeByExt(ext);
    console.log('[WINDOW_UTILS] File type detected:', { fileName, ext, appType });
    return appType;
  }

  console.log('[WINDOW_UTILS] No app type matched, returning null');
  return null;
};

export const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const getPreviewText = (note: Note) => {
  try {
    const content = note.skeleton?.content;
    if (!content) {
      return 'Empty Note';
    }

    if (typeof content === 'string') {
      return stripHtml(content).substring(0, 50) || 'Empty Note';
    }

    return 'Empty Note';
  } catch (e) {
    return 'Error loading note';
  }
};

const normalizeDisplayName = (name?: string) => {
  if (!name) return '';
  const trimmed = name.trim();
  // Only collapse duplicated file extensions (e.g., "file.pdffile.pdf"), not arbitrary text
  const len = trimmed.length;
  if (len > 1 && len % 2 === 0) {
    const half = len / 2;
    const first = trimmed.slice(0, half);
    const second = trimmed.slice(half);
    // Only collapse if it looks like a file extension duplication (contains a dot)
    if (first === second && first.includes('.')) return first;
  }
  return trimmed;
};

export const getEntityDisplayName = (entity: BaseEntityType) => {
  // ✅ FIX: Notes have entity.name just like all other entities - no special handling needed!
  if (entity.entityType === 'WebDocument') {
    return normalizeDisplayName((entity as any).skeleton.url);
  } else if (entity.entityType === 'Space') {
    return normalizeDisplayName(entity.name || `space_${entity._id}`);
  } else if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // Handle both legacy "File" and hierarchical "File.*" types
    // For files, render the exact file name without normalization to avoid
    // unintended collapsing in some render contexts (e.g., windowed mode)
    const display = (entity.skeleton as any)?.fileName || entity.name || 'Untitled';
    return display;
  } else {
    // Default: use entity.name for all entity types including Note, Doc, Group, etc.
    return normalizeDisplayName(entity.name);
  }
};

export const getEntityDisplayIcon = (entity: BaseEntityType) => {
  // Try to get icon from iconService first
  const globalIcon = iconService.getGlobalEntityIcon(entity.entityType);
  if (globalIcon && globalIcon.emoji) {
    return globalIcon.emoji;
  }
  
  // Fallback to hardcoded icons for backward compatibility
  const iconMap = {
    Space: '🃏',  // Cards emoji as fallback
    Group: '📁',
    Note: '📝',
    File: '📄',
    WebDocument: '🌐',
    Doc: '📄',
    Bank: '🏦',
  };
  
  return iconMap[entity.entityType as keyof typeof iconMap] || '⬜️'; // Default to file icon
};

export const getRandomWindowPositionShift = () => {
  const numbers = [-30, -20, -10, 10, 20, 30];
  return numbers[Math.floor(Math.random() * numbers.length)];
};

export const getWindowSizeAndPosition = () => {
  const size = { width: 600, height: 500 };
  const position = {
    x: window.innerWidth / 2 - size.width / 2 + getRandomWindowPositionShift(),
    y: window.innerHeight / 2 - size.height / 2 + getRandomWindowPositionShift(),
  };

  return { size, position };
};

export const fileAndGroupFilter = (entity: BaseEntityType) =>
  (entity.entityType === 'File' || entity.entityType?.startsWith('File.') || entity.entityType === 'Group') && !entity.isSystemGroup;

export const getWindowTitle = (window: WindowEntity): string => {
  // If there's no appType, fall back to the provided title
  if (!window.appType) return window.title || 'Untitled';

  const windowAny = window as any;
  
  // ✅ REFACTOR: Always use entityStore as single source of truth
  let currentEntity = null;
  
  if (window.entityLlmId) {
    const useEntityStore = require('../../../stores/entityStore').default;
    const entityStore = useEntityStore.getState();
    currentEntity = entityStore.entities.find((e: any) => e.llmId === window.entityLlmId);
  }
  
  // Fallback to embedded entity if lookup failed
  if (!currentEntity) {
    currentEntity = windowAny.entity;
  }

  // Check for application state for docs
  const windowWithState = window as WindowWithApplicationState;
  if (window.appType === 'docs' && windowWithState.applicationState?.docs) {
    const docsState = windowWithState.applicationState.docs;
    
    // If we're editing a document, use entity name as single source of truth
    if (docsState.currentState === 'editing' && docsState.activeDocId) {
      // PRIORITY 1: Get current entity name from entityStore (MOST RELIABLE)
      if (currentEntity?.name) {
        return currentEntity.name;
      }
      
      // PRIORITY 2: Try to get entity by activeDocId from entityStore
      const useEntityStore = require('../../../stores/entityStore').default;
      const entityStore = useEntityStore.getState();
      const entityByDocId = entityStore.entities.find((e: any) => 
        e._id === docsState.activeDocId || e.id === docsState.activeDocId
      );
      if (entityByDocId?.name) {
        return entityByDocId.name;
      }
      
      // FALLBACK: Use window title (should be kept in sync)
      if (window.title && window.title !== 'Untitled Document') {
        return window.title;
      }
      
      return 'Untitled Document';
    }
    
    // ✅ FIX: Handle overview mode explicitly
    if (docsState.currentState === 'overview') {
      return 'Documents';
    }
  }

  // For any entity-backed window, the entity name is authoritative.
  // This covers canvas-v2, notes-v2, sheet-v2, presentations-v2, browser, pdfium, etc.
  if (currentEntity?.name) {
    return currentEntity.name;
  }

  // For non-entity-based windows, use static title only if it's genuinely custom.
  // A title is NOT custom if it matches the canonical app name or was auto-generated
  // by capitalizing the raw appId (e.g. 'EntityBrowser2' from 'entityBrowser2').
  const canonicalTitle = getAppTitleByType(window.appType);
  const autoGeneratedTitle = window.appType.charAt(0).toUpperCase() + window.appType.slice(1);
  if (window.title && window.title !== canonicalTitle && window.title !== autoGeneratedTitle) {
    return window.title;
  }

  // For app-based windows, use the canonical app name
  return canonicalTitle;
};

export const getMentionDisplayName = (entity: AnyWindowEntity): string => {
  if (!entity) return 'Unknown';
  
  // For document windows, use the same logic as getWindowTitle to get the current document name
  const entityAny = entity as any;
  if (entityAny.appType === 'docs' && entityAny.applicationState?.docs) {
    const docsState = entityAny.applicationState.docs;
    
    // If we're editing a document, use its current name
    if (docsState.currentState === 'editing' && docsState.currentDocName) {
      return docsState.currentDocName;
    }
    
    // If we have an activeDocId but no currentDocName, try to find it in recentDocs
    if (docsState.activeDocId && docsState.recentDocs) {
      const activeDoc = docsState.recentDocs.find((doc: any) => doc.docId === docsState.activeDocId);
      if (activeDoc && activeDoc.name) {
        return activeDoc.name;
      }
    }
  }
  
  // For other entity types, use the existing logic
  if ('title' in entity && typeof entity.title === 'string') {
    return entity.title || 'Untitled';
  }
  
  if ('name' in entity && typeof entity.name === 'string') {
    return entity.name;
  }

  return entity.id || 'Unknown';
};